import { useEffect, useRef } from "react";
import { lastPushToken, registerPush } from "@cntrl/native";
import type { ApiClient } from "@cntrl/api-client";
import { APP_ENV, BUILD_NUMBER } from "../config/env.js";
import { useApp } from "./appContext.js";
import { useMe } from "./hooks/useSession.js";
import { useActingAs } from "./hooks/useDevFixtureSwitch.js";

/**
 * Register this device for the signed-in PERSON's push notifications (ART-259). A device registers for the person,
 * not for a band: it receives every notification addressed to that person, whichever band or org it is about. (It
 * used to register per band on launch and on every band switch, and the server read devices by band, so a person
 * only heard a band's pushes on a phone where they had opened that band.)
 *
 * The `@cntrl/native` `registerPush` runs the checkPermissions → requestPermissions → listeners-before-register
 * race-safe flow and, on the `registration` event, calls back here to POST the APNs token to `/me/push/register`.
 * A no-op on web (there is no device token in a browser).
 *
 * The POST NEVER throws out of this callback — it runs inside the async `registration` listener, so a failed
 * registration must not crash the app; the next launch retries (registration is an idempotent server-side upsert).
 */
export function makeRegisterPush(api: ApiClient): () => Promise<void> {
  return async () => {
    await registerPush({
      appEnv: APP_ENV,
      appVersion: BUILD_NUMBER,
      postToken: async (request) => {
        try {
          // openapi-fetch REJECTS on a transport failure (offline) — it only populates `error` for an HTTP
          // response — so the try/catch is what actually honours the no-throw contract for the common failure
          // mode; the `error` field covers a non-2xx HTTP response. Either way we swallow and retry next launch.
          await api.POST("/api/v1/me/push/register", {
            body: {
              token: request.token,
              platform: request.platform,
              appEnv: request.appEnv,
              deviceModel: request.deviceModel,
              appVersion: request.appVersion,
            },
          });
        } catch {
          // network / transport failure — retried on the next launch.
        }
      },
    });
  };
}

/**
 * Stop pushing to THIS device for the person signing out (ART-259 C8). Called by logout BEFORE the session ends,
 * while the bearer still names that person: the server soft-disables only the caller's row for this token. Without
 * it a handed-down or shared phone kept receiving the previous person's notifications after somebody else signed in.
 *
 * Best-effort and never throws — a logout must always complete. A no-op when the device never registered (web,
 * push permission denied). The next sign-in re-registers the device for whoever that is.
 */
export async function unregisterThisDevice(api: ApiClient): Promise<void> {
  const token = lastPushToken();
  if (!token) return;
  try {
    await api.POST("/api/v1/me/push/unregister", { body: { token } });
  } catch {
    // offline — the server keeps the row; the next person's sign-in still registers their own.
  }
}

/**
 * Register once per signed-in person, from each authed shell layout (the artist shells and the roster shell — a
 * manager who never opens an artist still hears their own notifications). Re-runs only when the PERSON changes.
 *
 * Never while the DEV "View as" switcher is wearing someone else: the device would be registered to the fixture
 * person and keep receiving their notifications after the swap ends.
 */
export function usePushRegistration(): void {
  const { api } = useApp();
  const personId = useMe().data?.person?.personId;
  const actingAs = useActingAs();
  const registeredFor = useRef<string | null>(null);

  useEffect(() => {
    if (!personId || actingAs || registeredFor.current === personId) return;
    registeredFor.current = personId;
    void makeRegisterPush(api)();
  }, [api, personId, actingAs]);
}
